const escapeHtml = (value) => String(value ?? "")
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#039;");

const clean = (value) => String(value || "").trim();
const fallbackMoney = (value) => String(value ?? "-");
const fallbackHours = (value) => String(value ?? "-");

export const buildPaystubPrintHtml = ({
  payment = {},
  row = {},
  period = {},
  employer = {},
  ytdTotals = {},
  staffName = "",
  staffEmail = "",
  staffPhone = "",
  employeeAddress = "",
  employerName = "",
  employerAddress = "",
  employerContact = "",
  timesheetCount = 0,
  money = fallbackMoney,
  formatHours = fallbackHours,
  readable = (value) => String(value || ""),
  payRateDisplayParts = () => [],
  computedPayrollTaxLinesFor = () => [],
  computedPayrollTaxAmountFor = () => 0,
  hasAnyPayRate = () => true,
  regularPayAmountFor = () => 0,
  overtimePayAmountFor = () => 0,
  payrollItemAmount = () => 0,
} = {}) => {
  const esc = escapeHtml;
  const currency = payment?.currency || row?.currency || "USD";
  const moneyText = (value) => money(value, currency);
  const resolvedStaffName = staffName || "Employee";
  const resolvedStaffContact = [staffEmail, staffPhone].map(clean).filter(Boolean).join(" | ") || "No contact on record";
  const resolvedEmployerName = employerName || employer?.name || payment?.workspaceName || "Organization";
  const resolvedEmployerAddress = employerAddress || employer?.address || payment?.workspaceAddress || "No organization address on record";
  const resolvedEmployerContact = employerContact || [employer?.email, employer?.phone].map(clean).filter(Boolean).join(" | ") || "No contact on record";
  const netPayText = hasAnyPayRate(row) ? moneyText(row.estimatedPay) : "-";
  const taxTotal = (row.preTaxDeductionsAmount || 0)
    + (row.postTaxDeductionsAmount || 0)
    + computedPayrollTaxAmountFor(row)
    + (row.federalTax?.amount || 0);
  const line = (label, detail, amount, total = false, extraClass = "") => `
      <div class="line${total ? " total" : ""}${extraClass ? ` ${extraClass}` : ""}">
        <span>${esc(label)}</span>
        <em>${esc(detail || "")}</em>
        <strong>${esc(moneyText(amount || 0))}</strong>
      </div>`;
  const earningsLines = [
    line(
      "Regular pay",
      String(row.payRateType || "").toUpperCase() === "SALARY"
        ? "Salary"
        : `${formatHours(row.regularHours)} x ${moneyText(row.hourlyRate)}`,
      regularPayAmountFor(row),
    ),
    row.overtimeHours > 0
      ? line("Overtime pay", `${formatHours(row.overtimeHours)} x ${moneyText(row.overtimeRate)}`, overtimePayAmountFor(row))
      : "",
    ...(row.activeEarnings || []).map((item) => line(
      item.name,
      `${item.scope === "USER" ? "Staff only" : "All staff"} | ${item.taxable === false ? "Not taxable" : "Taxable"}`,
      payrollItemAmount(item, row.basePay),
    )),
    line("Gross pay", "", row.grossPay, true),
  ].join("");
  const deductionLines = [
    ...(row.activeDeductions || []).map((item) => line(
      item.name,
      "",
      payrollItemAmount(item, row.grossPay),
    )),
    ...computedPayrollTaxLinesFor(row).map((item) => line(
      item.name,
      "",
      payrollItemAmount(item, row.grossPay),
    )),
    line("Federal income tax", "", row.federalTax?.amount || 0),
    line("Taxable pay", "", row.taxablePay, false, "taxable"),
    line("Total deductions and taxes", "", taxTotal, true),
  ].join("");
  const ytdSection = Object.keys(ytdTotals || {}).length
    ? `<section class="ytd section">
          <div class="summary-box"><span>YTD gross pay</span><strong>${esc(moneyText(ytdTotals.grossPay))}</strong></div>
          <div class="summary-box"><span>YTD net pay</span><strong>${esc(moneyText(ytdTotals.netPay))}</strong></div>
          <div class="summary-box"><span>YTD deductions</span><strong>${esc(moneyText(ytdTotals.totalDeductions))}</strong></div>
          <div class="summary-box"><span>YTD hours</span><strong>${esc(formatHours(ytdTotals.approvedHours))}</strong></div>
        </section>`
    : "";

  return `<!doctype html>
      <html>
        <head>
          <title>OCare Paystub</title>
          <style>
            @page { size: letter portrait; margin: 0.35in; }
            * { box-sizing: border-box; }
            html, body { background: #ffffff; color: #111827; font-family: Arial, sans-serif; margin: 0; }
            body { padding: 0; }
            .paystub { border: 1px solid #b7dfc5; border-radius: 8px; max-width: 100%; padding: 14px; }
            .parties, .header, .meta, .columns { display: grid; gap: 12px; }
            .parties { border-bottom: 1px solid #dceee3; grid-template-columns: repeat(2, minmax(0, 1fr)); margin-bottom: 14px; padding-bottom: 14px; }
            .header { border-bottom: 1px solid #dceee3; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(135px, 0.45fr); padding-bottom: 14px; }
            .meta { grid-template-columns: repeat(4, minmax(0, 1fr)); margin: 14px 0; }
            .columns { grid-template-columns: repeat(2, minmax(0, 1fr)); }
            .parties > *, .header > *, .meta > *, .columns > *, .box, .net, .table, .line > * { min-width: 0; }
            .box, .net, .table { border: 1px solid #dceee3; border-radius: 8px; padding: 10px; }
            .net { background: #ecfbef; text-align: right; }
            small, .label { color: #2385db; display: block; font-size: 11px; font-weight: 700; text-transform: uppercase; }
            h1, h2, h3, p { margin: 0; }
            h1 { color: #0e5604; font-size: 20px; margin-bottom: 4px; }
            h2 { color: #0e5604; font-size: 16px; margin: 2px 0 5px; }
            h3 { color: #0e5604; font-size: 14px; margin: 2px 0 4px; }
            p { color: #4b5563; font-size: 12px; line-height: 1.35; overflow-wrap: anywhere; word-break: break-word; }
            .net strong { color: #0e5604; display: block; font-size: 23px; line-height: 1.1; margin-top: 6px; overflow-wrap: anywhere; word-break: break-word; }
            .meta .box strong { color: #062701; display: block; font-size: 14px; margin-top: 4px; overflow-wrap: anywhere; }
            .rate-lines em { color: #062701; display: block; font-style: normal; font-weight: 700; }
            .table { padding: 0; overflow: hidden; }
            .table-title { background: #f0fff4; border-bottom: 1px solid #b7dfc5; color: #0e5604; font-weight: 800; padding: 10px; }
            .line { align-items: start; border-bottom: 1px solid #e8f2ec; display: grid; gap: 8px; grid-template-columns: minmax(0, 1fr) minmax(0, 0.95fr) minmax(0, 78px); padding: 9px 10px; }
            .line:last-child { border-bottom: 0; }
            .line span, .line em, .line strong { font-size: 12px; overflow-wrap: anywhere; word-break: break-word; }
            .line em { color: #4b5563; font-style: normal; }
            .line strong { color: #2385db; font-weight: 500; text-align: right; }
            .line.taxable { background: #eaf5ff; }
            .line.taxable span, .line.taxable strong { color: #0b4f93; font-weight: 800; }
            .line.total { background: #f6fbf9; }
            .line.total span, .line.total strong { color: #0e5604; font-weight: 800; }
            .bottom-summary { display: grid; gap: 10px; grid-template-columns: repeat(2, minmax(0, 1fr)); margin-top: 12px; }
            .summary-box { background: #f6fbf9; border: 1px solid #b7dfc5; border-radius: 8px; padding: 10px; text-align: right; }
            .summary-box span { color: #2385db; display: block; font-size: 11px; font-weight: 800; text-transform: uppercase; }
            .summary-box strong { color: #0e5604; display: block; font-size: 20px; margin-top: 4px; overflow-wrap: anywhere; word-break: break-word; }
            .ytd { display: grid; gap: 10px; grid-template-columns: repeat(4, minmax(0, 1fr)); margin-top: 12px; }
            .ytd .summary-box { text-align: left; }
            .ytd .summary-box strong { font-size: 15px; }
            .section { break-inside: avoid; page-break-inside: avoid; }
            @media print {
              .paystub { border-color: #d1d5db; }
              .table-title { background: #f3f4f6; color: #111827; }
            }
          </style>
        </head>
        <body>
          <main class="paystub">
            <section class="parties section">
              <div class="box">
                <small>Employer</small>
                <h2>${esc(resolvedEmployerName)}</h2>
                <p>${esc(resolvedEmployerAddress)}</p>
                <p>${esc(resolvedEmployerContact)}</p>
              </div>
              <div class="box">
                <small>Employee</small>
                <h2>${esc(resolvedStaffName)}</h2>
                <p>${esc(employeeAddress || "No address on record")}</p>
                <p>${esc(resolvedStaffContact)}</p>
              </div>
            </section>
            <section class="header section">
              <div>
                <span class="label">Employee</span>
                <h1>${esc(resolvedStaffName)}</h1>
                <p>${esc(resolvedStaffContact)}</p>
              </div>
              <div>
                <span class="label">Pay period</span>
                <h1>${esc(`${period.start || "-"} - ${period.end || "-"}`)}</h1>
                <p>${esc(`${period.payDate ? `Pay date ${period.payDate} | ` : ""}${period.frequency ? `${readable(period.frequency, "Schedule")} | ` : ""}${timesheetCount} approved timesheet${timesheetCount === 1 ? "" : "s"}`)}</p>
              </div>
              <div class="net">
                <span class="label">Net pay</span>
                <strong>${esc(netPayText)}</strong>
              </div>
            </section>
            <section class="meta section">
              <div class="box"><small>Approved hours</small><strong>${esc(formatHours(row.approvedHours))}</strong></div>
              <div class="box"><small>Regular</small><strong>${esc(formatHours(row.regularHours))}</strong></div>
              <div class="box"><small>Overtime</small><strong>${esc(formatHours(row.overtimeHours))}</strong></div>
              <div class="box"><small>Pay rate</small><strong class="rate-lines">${payRateDisplayParts(row).map((label) => `<em>${esc(label)}</em>`).join("")}</strong></div>
            </section>
            <section class="columns section">
              <div class="table">
                <div class="table-title">Earnings</div>
                ${earningsLines}
              </div>
              <div class="table">
                <div class="table-title">Taxes and deductions</div>
                ${deductionLines}
              </div>
            </section>
            <section class="bottom-summary section">
              <div class="summary-box">
                <span>Gross pay</span>
                <strong>${esc(moneyText(row.grossPay))}</strong>
              </div>
              <div class="summary-box">
                <span>Net pay</span>
                <strong>${esc(netPayText)}</strong>
              </div>
            </section>
            ${ytdSection}
          </main>
        </body>
      </html>`;
};

export const printPaystubDocument = (paystubOptions = {}) => {
  const printWindow = window.open("", "_blank", "width=980,height=1200");
  if (!printWindow) return false;
  printWindow.document.write(buildPaystubPrintHtml(paystubOptions));
  printWindow.document.close();
  try {
    printWindow.history.replaceState(null, "OCare Paystub", "/ocare-paystub");
  } catch (_error) {
    // The visible OCare footer remains available if the browser blocks URL replacement.
  }
  printWindow.focus();
  printWindow.setTimeout(() => {
    printWindow.print();
  }, 250);
  return true;
};
